import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import CampoTexto from '@/components/base/CampoTexto';
import {
  ajustarMensagemConvite,
  apenasDigitos,
  linkWhatsApp,
  mascararTelefone,
  funcTexto,
  normalizarCaixa,
  rotuloCaixaOpcao,
  type CaixaEquipe,
  type Notificar,
  type PontoEquipe,
} from './utils';

interface Props {
  eventoId: number;
  eventoNome: string;
  pontos: PontoEquipe[];
  temLoja: boolean;
  inicial?: { caixa?: boolean; pontos?: number[]; portaria?: boolean };
  aoFechar: () => void;
  aoSalvo: () => void;
  notificar: Notificar;
}

interface Resultado {
  link: string;
  whatsapp: string | null;
  mensagem: string;
  funcoes: string;
  convite_id: number | null;
}

interface Opcao {
  chave: string;
  rotulo: string;
  emoji: string;
  caixa?: boolean;
  ponto?: number;
  portaria?: boolean;
}

// Botão-pílula de múltipla escolha (funções do convite).
function ChipEscolha({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[14px] font-semibold transition-colors ${
        ativo
          ? 'border-primary-500 bg-primary-500 text-white'
          : 'border-secondary-300 bg-white text-foreground-800 hover:bg-background-100'
      }`}
    >
      {children}
    </button>
  );
}

export default function FolhaChamar({
  eventoId,
  eventoNome,
  pontos,
  temLoja,
  inicial,
  aoFechar,
  aoSalvo,
  notificar,
}: Props) {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [caixa, setCaixa] = useState(inicial?.caixa === true);
  const [selecionados, setSelecionados] = useState<number[]>(inicial?.pontos ?? []);
  const [portaria, setPortaria] = useState(inicial?.portaria === true);

  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [copiado, setCopiado] = useState(false);

  const [caixasDoEvento, setCaixasDoEvento] = useState<CaixaEquipe[]>([]);
  const [caixaEscolhido, setCaixaEscolhido] = useState<number | null>(null);

  // "Qual caixa?": a mesma lista de caixas ativos do evento.
  useEffect(() => {
    let ativo = true;
    (async () => {
      const { data, error } = await supabase.rpc('caixa_caixas_do_evento', { p_evento: eventoId });
      if (!ativo) return;
      if (!error) {
        const lista = ((data as Record<string, unknown>[]) || [])
          .map(normalizarCaixa)
          .filter((c) => c.ativo);
        setCaixasDoEvento(lista);
        if (lista.length === 1) setCaixaEscolhido(lista[0].caixa);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const opcoes: Opcao[] = [
    ...(temLoja ? [{ chave: 'caixa', rotulo: 'Caixa', emoji: '💳', caixa: true } as Opcao] : []),
    ...pontos.map((p) => ({ chave: `ponto-${p.ponto_id}`, rotulo: p.nome, emoji: '🍹', ponto: p.ponto_id })),
    { chave: 'portaria', rotulo: 'Portaria', emoji: '🎟', portaria: true },
  ];

  function alternar(op: Opcao) {
    if (op.caixa) setCaixa((v) => !v);
    else if (op.portaria) setPortaria((v) => !v);
    else if (op.ponto != null) {
      setSelecionados((atual) =>
        atual.includes(op.ponto as number)
          ? atual.filter((x) => x !== op.ponto)
          : [...atual, op.ponto as number],
      );
    }
  }

  function ativo(op: Opcao): boolean {
    if (op.caixa) return caixa;
    if (op.portaria) return portaria;
    return op.ponto != null && selecionados.includes(op.ponto);
  }

  async function criar() {
    if (!nome.trim()) {
      setErro('Digite o nome da pessoa.');
      return;
    }
    if (!caixa && selecionados.length === 0 && !portaria) {
      setErro('Escolha ao menos uma função.');
      return;
    }
    setSalvando(true);
    setErro(null);
    const digitos = apenasDigitos(telefone);
    try {
      const { data, error } = await supabase.rpc('equipe_criar_convite', {
        p_evento: eventoId,
        p_nome: nome.trim(),
        p_telefone: digitos || null,
        p_caixa: caixa,
        p_pontos: selecionados,
        p_portaria: portaria,
      });
      if (error) throw error;
      const resp = data as {
        ok?: boolean;
        erro?: string;
        link?: string;
        whatsapp?: string | null;
        mensagem?: string;
        funcoes?: unknown;
        convite_id?: number;
      };
      if (!resp || resp.ok === false) {
        notificar(resp?.erro || 'Não foi possível criar o convite.', 'erro');
        return;
      }
      setResultado({
        link: resp.link ?? '',
        whatsapp: resp.whatsapp ?? null,
        mensagem: ajustarMensagemConvite(resp.mensagem ?? ''),
        funcoes: funcTexto(resp.funcoes),
        convite_id: resp.convite_id ?? null,
      });
      aoSalvo();
      // Caixa escolhido na hora: grava no convite (se falhar, o convite continua criado).
      if (caixa && caixaEscolhido != null && resp.convite_id) {
        const { data: dc, error: ec } = await supabase.rpc('equipe_definir_caixa', {
          p_convite: resp.convite_id,
          p_caixa: caixaEscolhido,
        });
        if (!ec) {
          const d = dc as { ok?: boolean; erro?: string } | null;
          if (d && d.ok === false) {
            notificar(d.erro ?? 'Não consegui escolher o caixa. Use "Trocar caixa" na lista.', 'erro');
          }
        }
      }
    } catch (e) {
      notificar(mensagemBanco(e), 'erro');
    } finally {
      setSalvando(false);
    }
  }

  function abrirWhatsApp() {
    if (!resultado) return;
    const url = linkWhatsApp(resultado.whatsapp, resultado.mensagem);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function copiarLink() {
    if (!resultado?.link) return;
    try {
      await navigator.clipboard.writeText(resultado.link);
    } catch {
      /* silencioso */
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <Modal aberto titulo="Chamar pessoa" aoFechar={aoFechar}>
      {resultado ? (
        <div>
          <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-3">
            <p className="flex items-center gap-2 text-[15px] font-semibold text-success-800">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-checkbox-circle-line" />
              </span>
              Convite criado
            </p>
            {resultado.funcoes && (
              <p className="mt-1 text-[14px] text-success-800/90">{resultado.funcoes}</p>
            )}
          </div>

          <div className="mt-4 space-y-2">
            <Botao variante="whatsapp" tamanho="lg" icone="ri-whatsapp-line" cheio onClick={abrirWhatsApp}>
              Mandar no WhatsApp
            </Botao>
            <Botao variante="secundario" tamanho="md" icone={copiado ? 'ri-check-line' : 'ri-file-copy-line'} cheio onClick={copiarLink}>
              {copiado ? 'Link copiado ✓' : 'Copiar link'}
            </Botao>
          </div>

          <p className="mt-3 text-[13px] leading-relaxed text-foreground-500">
            O link vale só para uma pessoa. Quando ela aceitar, aparece aqui.
          </p>

          <button
            type="button"
            onClick={aoFechar}
            className="mt-4 flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl py-3 text-[14px] font-semibold text-foreground-500 transition-colors hover:text-foreground-800"
          >
            Fechar
          </button>
        </div>
      ) : (
        <div>
          <CampoTexto
            id="equipe-chamar-nome"
            name="nome"
            label="Nome"
            value={nome}
            onChange={setNome}
            required
            autoFocus
            placeholder="ex.: Maria Souza"
          />

          <div className="mt-4">
            <CampoTexto
              id="equipe-chamar-telefone"
              name="telefone"
              label="WhatsApp (opcional)"
              help="Se preencher, o botão do WhatsApp já abre a conversa dela."
              value={telefone}
              onChange={(v) => setTelefone(mascararTelefone(v))}
              inputMode="tel"
              placeholder="(14) 9 9999-9999"
            />
          </div>

          <p className="mt-5 text-[15px] font-semibold text-foreground-950">O que vai fazer?</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {opcoes.map((op) => (
              <ChipEscolha key={op.chave} ativo={ativo(op)} onClick={() => alternar(op)}>
                <span aria-hidden>{op.emoji}</span>
                {op.rotulo}
              </ChipEscolha>
            ))}
          </div>

          {caixa && (
            <div className="mt-4">
              <p className="text-[15px] font-semibold text-foreground-950">Qual caixa?</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {caixasDoEvento.map((c) => (
                  <ChipEscolha
                    key={c.caixa}
                    ativo={caixaEscolhido === c.caixa}
                    onClick={() => setCaixaEscolhido(c.caixa)}
                  >
                    {rotuloCaixaOpcao(c)}
                  </ChipEscolha>
                ))}
                <ChipEscolha ativo={caixaEscolhido == null} onClick={() => setCaixaEscolhido(null)}>
                  Escolher na hora
                </ChipEscolha>
              </div>
            </div>
          )}

          {!temLoja && (
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-500">
              Este evento não tem caixa nem barracas. Para chamar caixa ou barraca, cadastre na aba Loja.
            </p>
          )}

          {erro && (
            <p className="mt-3 flex items-center gap-1.5 text-[14px] text-danger-600" role="alert">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                <i className="ri-error-warning-line" />
              </span>
              {erro}
            </p>
          )}

          <div className="mt-5">
            <Botao tamanho="lg" cheio carregando={salvando} onClick={criar}>
              {salvando ? 'Criando…' : 'Criar convite'}
            </Botao>
          </div>
          <p className="mt-3 text-[13px] leading-relaxed text-foreground-500">
            Vamos gerar um link único para {eventoNome}. A pessoa toca no link e a tela dela abre
            sozinha — sem criar conta.
          </p>
        </div>
      )}
    </Modal>
  );
}